// Shared loader for /api/v2/rhg/hotel-detail.
//
// The M&E slide stores only an EMMA code and the author's overrides, so the
// hotel payload is fetched every time a slide is viewed rather than frozen
// into the slide JSON. A deck routinely carries several slides for the same
// hotel, and the response is 230-450 KB, so the in-flight promise is shared
// per code: ten slides for one hotel cost one request.
//
// Deliberately not persisted anywhere. "Fully dynamic" means a view always
// reflects what the API says now; a cache that outlived the page would put
// the stale-snapshot problem back, just somewhere harder to see.

import { serverUrl } from "../../config";

// The six properties the At a glance (M&E) layout reads. hotel-detail serves
// thirteen; the other seven - rooms, roomGalleries and the five
// spreadsheet-imported blocks - are 73 KB this layout never looks at, and it
// now pays for them on every view rather than once at authoring time.
export const AAG_ME_PARTS = [
  "hotel",
  "roomInfo",
  "gallery",
  "restaurants",
  "images",
  "ota",
];

// The three properties the standard At a glance reads. It needs fewer than
// the M&E layout: no gallery, no restaurants, no OTA.
export const AAG_PARTS = ["hotel", "roomInfo", "images"];

const inFlight = new Map();
const resolved = new Map();

export const normaliseCode = (code) =>
  typeof code === "string" ? code.trim().toUpperCase() : "";

// Two callers asking for different parts must not share an entry, or the
// second gets a payload missing the properties it asked for.
const cacheKey = (code, parts) =>
  `${normaliseCode(code)}|${(parts || []).join(",")}`;

// Synchronous read for callers that render before they can await - returns
// undefined until the fetch for that code has landed.
export const peekHotelDetail = (code, parts = AAG_ME_PARTS) =>
  resolved.get(cacheKey(code, parts));

export const fetchHotelDetail = (code, parts = AAG_ME_PARTS) => {
  const name = normaliseCode(code);
  if (!name) return Promise.reject(new Error("No hotel code"));

  const key = cacheKey(name, parts);
  if (resolved.has(key)) return Promise.resolve(resolved.get(key));
  if (inFlight.has(key)) return inFlight.get(key);

  const query = [
    `code=${encodeURIComponent(name)}`,
    parts?.length ? `parts=${encodeURIComponent(parts.join(","))}` : "",
    "_format=json",
  ]
    .filter(Boolean)
    .join("&");

  const request = fetch(`${serverUrl}/api/v2/rhg/hotel-detail?${query}`, {
    credentials: "include",
  })
    .then(async (response) => {
      const json = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(json?.error || `HTTP ${response.status}`);
      }
      return json;
    })
    .then((json) => {
      resolved.set(key, json);
      inFlight.delete(key);
      return json;
    })
    .catch((error) => {
      // Not cached: a failure is per-attempt, so the next view of the same
      // hotel retries rather than inheriting a dead slide for the session.
      inFlight.delete(key);
      throw error;
    });

  inFlight.set(key, request);
  return request;
};

// Every hotel payload a deck has asked for, so a thumbnail run or an export
// can wait for the page to settle instead of guessing at a delay.
export const pendingHotelDetails = () => Array.from(inFlight.values());
